import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { pushDescriptionToJTL, type BlockType } from '@/lib/api/descriptions'
import { BULLET_KEYS, type BulletKey } from '@/components/products/v2/tabs/content/channels/channel-constants'

interface SaveArgs {
  bulletFields: Record<BulletKey, string>
  bulletsDirty: Record<BulletKey, boolean>
  keywordsDirty: boolean
  keywordsValue: string
}

/**
 * Schreibt dirty Channel-Bullets/Keywords über den JTL-Push-Port (Custom-Fields).
 * saveChannelId = channel_package-id (wie der C1-Beschreibungs-Save);
 * readChannelId = channels.id-UUID (Query-Key der Bullets-Query, für Invalidierung).
 */
export function useChannelBulletsSave(
  productId: string, saveChannelId: string, readChannelId: string, lang: string,
) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [isSaving, setIsSaving] = useState(false)

  const save = async ({ bulletFields, bulletsDirty, keywordsDirty, keywordsValue }: SaveArgs): Promise<boolean> => {
    setIsSaving(true)
    try {
      const jobs: { block_type: string; content: string }[] = BULLET_KEYS
        .filter((k) => bulletsDirty[k])
        .map((k) => ({ block_type: k, content: bulletFields[k] }))
      if (keywordsDirty) jobs.push({ block_type: 'SeoMetaKeywords', content: keywordsValue })
      if (jobs.length === 0) return true
      for (const job of jobs) {
        const res = await pushDescriptionToJTL(productId, {
          sales_channel_id: saveChannelId, language_iso: lang,
          block_type: job.block_type as BlockType, content: job.content,
        })
        if (!res.success) { toast.error(t('productContent.channels.pushError')); return false }
      }
      toast.success(t('productContent.channels.pushSuccess'))
      queryClient.invalidateQueries({ queryKey: ['channel-bullets', productId, readChannelId] })
      return true
    } catch {
      toast.error(t('productContent.channels.pushError'))
      return false
    } finally {
      setIsSaving(false)
    }
  }

  return { save, isSaving }
}
